// Lazy PostHog loader — splits posthog-js (~61 KB gz) out of the entry chunk.
//
// PostHog only runs after the user accepts the cookie consent banner (GDPR).
// Until then, the SDK should not be downloaded at all. This module hides the
// dynamic `import('posthog-js')` behind a small, sync-friendly facade.
//
// Design:
//   - `ensurePostHog()` triggers the dynamic import on first call and returns
//     a cached promise. Idempotent — subsequent callers join the same promise.
//   - `getPostHog()` returns the loaded module synchronously, or `null` when
//     the SDK has not yet been loaded. Useful for fire-and-forget call sites
//     that want to noop until consent is granted.
//   - Pending capture/identify calls made before the SDK loads are queued and
//     drained as soon as `posthog.init` resolves. This keeps the legacy sync
//     `track()` / `identify()` API working without forcing every call site to
//     become async.

import type { PostHog } from 'posthog-js';

let _posthog: PostHog | null = null;
let _loadPromise: Promise<PostHog> | null = null;

type QueuedCall =
  | { kind: 'capture'; event: string; properties?: Record<string, unknown> }
  | { kind: 'identify'; userId: string; properties?: Record<string, unknown> }
  | { kind: 'reset' }
  | { kind: 'startSessionRecording' }
  | { kind: 'stopSessionRecording' }
  | { kind: 'opt_in_capturing' }
  | { kind: 'opt_out_capturing' };

const _queue: QueuedCall[] = [];

/**
 * Begin (or return the in-flight promise for) loading posthog-js. Safe to call
 * many times — the dynamic import resolves once per page load.
 */
export function ensurePostHog(): Promise<PostHog> {
  if (_posthog) return Promise.resolve(_posthog);
  if (_loadPromise) return _loadPromise;
  _loadPromise = import('posthog-js')
    .then((mod) => {
      _posthog = mod.default;
      drainQueue();
      return _posthog;
    })
    .catch((err) => {
      // Surface the failure to the next caller — reset state so a later
      // attempt can retry the import (e.g. transient network blip).
      _loadPromise = null;
      throw err;
    });
  return _loadPromise;
}

/** Synchronously read the cached PostHog SDK reference, or null if not loaded yet. */
export function getPostHog(): PostHog | null {
  return _posthog;
}

/** Queue a call to be replayed when the SDK finishes loading. */
export function queuePostHogCall(call: QueuedCall): void {
  _queue.push(call);
}

function drainQueue() {
  if (!_posthog) return;
  while (_queue.length > 0) {
    const call = _queue.shift()!;
    try {
      switch (call.kind) {
        case 'capture':
          _posthog.capture(call.event, call.properties);
          break;
        case 'identify':
          _posthog.identify(call.userId, call.properties);
          break;
        case 'reset':
          _posthog.reset();
          break;
        case 'startSessionRecording':
          _posthog.startSessionRecording?.();
          break;
        case 'stopSessionRecording':
          _posthog.stopSessionRecording?.();
          break;
        case 'opt_in_capturing':
          _posthog.opt_in_capturing?.();
          break;
        case 'opt_out_capturing':
          _posthog.opt_out_capturing?.();
          break;
      }
    } catch {
      // Drop failed queued calls silently — analytics is best-effort.
    }
  }
}

/** Test-only — clears module state between tests. */
export function __resetPostHogLazyForTests(): void {
  _posthog = null;
  _loadPromise = null;
  _queue.length = 0;
}
